/**
 * replayIntelligence.ts — MER Algo Footprints v8.3
 *
 * Deterministic Replay Intelligence layer.
 *
 * ── Input contract ────────────────────────────────────────────────────────────
 *
 * Primary path:  ReplayIntelligenceSnapshot[] — normalized from ResearchSnapshot[]
 *                (saved by captureAndPersistDecisionSnapshot in the thesis panel)
 *
 * Fallback path: ThesisHistoryEntry[] — from getThesis(symbol).history
 *                Converted to ReplayIntelligenceSnapshot[] via fromThesisHistory()
 *
 * All replay-safe snapshots are serializable and deterministic from their fields.
 *
 * ── Determinism guarantee ─────────────────────────────────────────────────────
 *
 * analyseSnapshots(symbol, snapshots) is a PURE FUNCTION.
 * The output does NOT contain any runtime-generated values (no Date.now()).
 * computedAt is surfaced separately in ReplayIntelligenceResult (the wrapper) for
 * UI/cache purposes only — it is not part of the deterministic ReplayIntelligenceOutput.
 *
 * ── Structured-first event extraction ────────────────────────────────────────
 *
 * Events are derived from structured snapshot fields in strict priority order:
 *   1. Structured engine fields (regime, trap, bias, conviction, etc.)
 *   2. Text keywords — only for events where structured data is unavailable
 *
 * ── Event detection rules ─────────────────────────────────────────────────────
 * RULE_BIAS_FLIP          — thesis.bias changed between consecutive snapshots
 * RULE_REGIME_CHANGE      — marketState.regimeDominant changed
 * RULE_CONF_DROP          — conviction dropped by ≥ CONFIDENCE_DROP_MIN
 * RULE_CONF_SURGE         — conviction rose by ≥ CONFIDENCE_SURGE_MIN
 * RULE_ACT_SURGE          — actionability crossed above ACT_HIGH_THRESHOLD
 * RULE_ACT_DROP           — actionability crossed below ACT_LOW_THRESHOLD
 * RULE_TRAP_TRIGGERED     — trapCount increased (structured) OR trapConflict appeared
 * RULE_TRAP_CLEARED       — trapCount fell to zero after being > 0
 * RULE_GAMMA_POSTURE_SHIFT— gammaRegime changed
 * RULE_VOLATILITY_CHANGE  — volatilityRegime changed
 * RULE_MARKET_STATE_SHIFT — marketState.label changed
 * RULE_BREAKOUT_TEXT      — (fallback only) breakout keyword in summary text
 *
 * ── Turning point classification ──────────────────────────────────────────────
 * Turning point = ≥2 distinct event types at same snapshot.
 * Severity: 'critical' (≥3 types), 'major' (2 types).
 *
 * ── Replay-safe serialisation ────────────────────────────────────────────────
 * ReplayIntelligenceOutput is fully serializable (no functions, no circular refs).
 * Wrap in ReplayIntelligenceResult to add runtime metadata (computedAt).
 */

import type { ThesisHistoryEntry } from '../ai/thesis/thesisEngine';
import type { ResearchSnapshot }   from '../journal/services/researchSnapshot';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const REPLAY_INTELLIGENCE_THRESHOLDS = {
  // Conviction/confidence change magnitude
  CONFIDENCE_DROP_MIN:          15,
  CONFIDENCE_SURGE_MIN:         15,
  // Actionability thresholds
  ACT_HIGH_THRESHOLD:           60,
  ACT_LOW_THRESHOLD:            40,
  // Turning point significance
  TURNING_POINT_CRITICAL:       3,
  TURNING_POINT_MAJOR:          2,
  // Minimum snapshots for a meaningful summary
  MIN_ENTRIES_FOR_SUMMARY:      2,
  // Conviction trend: Δ between first and second half averages
  CONVICTION_TREND_DELTA:       8,
  // Wall proximity change (absolute price) to count as 'wall shift'
  WALL_SHIFT_MIN_PCT:           0.005,  // 0.5% of price
} as const;

const T = REPLAY_INTELLIGENCE_THRESHOLDS;

// ── Normalized replay snapshot type ──────────────────────────────────────────
//
// All replay intelligence computation works against this flat normalized type.
// Adapters from ResearchSnapshot and ThesisHistoryEntry live at the bottom.

export interface ReplayIntelligenceSnapshot {
  // Identity
  ts:     number;        // Unix ms — primary sort key
  symbol: string;
  price:  number | null;

  // Thesis-level structured fields
  bias:          string;   // 'bullish' | 'bearish' | 'neutral'
  conviction:    number;   // 0–100
  actionability: number;   // 0–100
  marketState:   string;   // human label (e.g. 'trending', 'consolidating')
  summary:       string;   // one-line summary for fallback text extraction

  // Market regime (from ResearchSnapshot.marketState)
  regimeDominant:    string | null;
  regimeLabel:       string | null;
  regimeConfidence:  number | null;

  // Gamma structured fields
  gammaRegime:   string | null;   // e.g. 'Long Gamma / Pinning'
  gammaFlip:     number | null;

  // Trap structured fields
  trapCount:     number;          // count of active trap signals at snapshot time
  trapConflict:  boolean;         // whether trap conflicts with setup direction

  // Volatility context
  volatilityRegime: string | null;  // 'low_vol' | 'normal_vol' | 'high_vol' | 'extreme_vol'

  // Structure evolution fields
  callWall:      number | null;
  putWall:       number | null;
  flipZone:      number | null;
}

// ── Output types ──────────────────────────────────────────────────────────────

export type ReplayEventType =
  | 'bias_flip'
  | 'regime_change'
  | 'confidence_drop'
  | 'confidence_surge'
  | 'actionability_surge'
  | 'actionability_drop'
  | 'trap_triggered'
  | 'trap_cleared'
  | 'gamma_posture_change'
  | 'volatility_regime_change'
  | 'market_state_shift'
  | 'wall_shift'
  | 'breakout_text';    // text-fallback only

export type EventSeverity = 'info' | 'medium' | 'high';

export interface ReplayEvent {
  snapshotIndex: number;    // index in sorted snapshot array — deterministic
  ts:            number;
  type:          ReplayEventType;
  severity:      EventSeverity;
  rule:          string;    // named rule identifier
  reason:        string;    // human-readable explanation
  source:        'structured' | 'text_fallback';
  context: {
    bias:          string;
    conviction:    number;
    actionability: number;
    marketState:   string;
  };
}

export type TurningPointSeverity = 'critical' | 'major';

export interface TurningPoint {
  snapshotIndex: number;
  ts:            number;
  severity:      TurningPointSeverity;
  events:        ReplayEvent[];
  narrative:     string;
}

export type ConvictionTrend = 'rising' | 'falling' | 'stable';

export interface ReplayIntelligenceSummary {
  symbol:              string;
  entryCount:          number;
  sessionLabel:        string;
  dominantBias:        string;
  dominantRegime:      string | null;
  dominantVolRegime:   string | null;
  avgConviction:       number;
  peakConviction:      number;
  troughConviction:    number;
  convictionTrend:     ConvictionTrend;
  majorTurningPoint:   TurningPoint | null;
  highestRiskSnapshot: ReplayIntelligenceSnapshot | null;
  strongestSnapshot:   ReplayIntelligenceSnapshot | null;
  eventCount:          number;
  turningPointCount:   number;
  structureEvo: {
    callWallRange:  [number, number] | null;   // [min, max] observed
    putWallRange:   [number, number] | null;
    flipZoneShifts: number;                    // count of distinct flip zone values
    wallShiftEvents: number;
  };
  lessons:   string[];
  reasoning: string[];
}

/** Deterministic replay output — no runtime timestamps inside */
export interface ReplayIntelligenceOutput {
  symbol:        string;
  snapshotCount: number;
  events:        ReplayEvent[];
  turningPoints: TurningPoint[];
  summary:       ReplayIntelligenceSummary | null;
}

/** Wrapper for the deterministic output — adds runtime metadata */
export interface ReplayIntelligenceResult {
  computedAt: number;          // Date.now() — NOT part of deterministic output
  output:     ReplayIntelligenceOutput;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round1(v: number): number { return Math.round(v * 10) / 10; }

const BREAKOUT_KEYWORDS = ['breakout', 'break out', 'break above', 'break below', 'expansion', 'confirmed break'];

function _hasBreakoutText(text: string): boolean {
  const lower = text.toLowerCase();
  return BREAKOUT_KEYWORDS.some(k => lower.includes(k));
}

// ── Step 1: Event extraction ──────────────────────────────────────────────────

function _extractEvents(snapshots: ReplayIntelligenceSnapshot[]): ReplayEvent[] {
  if (snapshots.length < 2) return [];
  const events: ReplayEvent[] = [];

  for (let i = 1; i < snapshots.length; i++) {
    const prev = snapshots[i - 1];
    const curr = snapshots[i];
    const ctx  = { bias: curr.bias, conviction: curr.conviction, actionability: curr.actionability, marketState: curr.marketState };
    const idx  = i;

    const push = (
      type: ReplayEventType, severity: EventSeverity,
      rule: string, reason: string,
      source: 'structured' | 'text_fallback' = 'structured',
    ) => events.push({ snapshotIndex: idx, ts: curr.ts, type, severity, rule, reason, source, context: ctx });

    // ── STRUCTURED EVENTS (primary) ──────────────────────────────────────────

    // RULE_BIAS_FLIP
    if (prev.bias !== curr.bias) {
      const isBullBear = (prev.bias === 'bullish' && curr.bias === 'bearish') || (prev.bias === 'bearish' && curr.bias === 'bullish');
      push('bias_flip', isBullBear ? 'high' : 'medium',
        'RULE_BIAS_FLIP',
        `Directional bias: ${prev.bias.toUpperCase()} → ${curr.bias.toUpperCase()}`);
    }

    // RULE_REGIME_CHANGE
    if (prev.regimeDominant && curr.regimeDominant && prev.regimeDominant !== curr.regimeDominant) {
      push('regime_change', 'high',
        'RULE_REGIME_CHANGE',
        `Regime: ${prev.regimeDominant} → ${curr.regimeDominant}`);
    }

    // RULE_CONF_DROP / SURGE
    const convΔ = curr.conviction - prev.conviction;
    if (convΔ <= -T.CONFIDENCE_DROP_MIN) {
      push('confidence_drop', Math.abs(convΔ) >= T.CONFIDENCE_DROP_MIN * 2 ? 'high' : 'medium',
        'RULE_CONF_DROP',
        `Conviction: ${prev.conviction}% → ${curr.conviction}% (Δ${convΔ})`);
    }
    if (convΔ >= T.CONFIDENCE_SURGE_MIN) {
      push('confidence_surge', 'medium',
        'RULE_CONF_SURGE',
        `Conviction: ${prev.conviction}% → ${curr.conviction}% (+${convΔ})`);
    }

    // RULE_ACT_SURGE / DROP
    if (prev.actionability < T.ACT_HIGH_THRESHOLD && curr.actionability >= T.ACT_HIGH_THRESHOLD) {
      push('actionability_surge', 'high',
        'RULE_ACT_SURGE',
        `Actionability entered actionable zone: ${prev.actionability}% → ${curr.actionability}%`);
    }
    if (prev.actionability >= T.ACT_HIGH_THRESHOLD && curr.actionability < T.ACT_LOW_THRESHOLD) {
      push('actionability_drop', 'high',
        'RULE_ACT_DROP',
        `Actionability exited actionable zone: ${prev.actionability}% → ${curr.actionability}%`);
    }

    // RULE_TRAP_TRIGGERED (structured — trapCount or trapConflict)
    if ((curr.trapCount > 0 && prev.trapCount === 0) || (!prev.trapConflict && curr.trapConflict)) {
      push('trap_triggered', 'high',
        'RULE_TRAP_TRIGGERED',
        `Trap condition emerged: count=${curr.trapCount}, conflict=${curr.trapConflict}`);
    }
    // RULE_TRAP_CLEARED
    if (prev.trapCount > 0 && curr.trapCount === 0 && !curr.trapConflict) {
      push('trap_cleared', 'medium',
        'RULE_TRAP_CLEARED',
        `Trap conditions cleared (was: count=${prev.trapCount})`);
    }

    // RULE_GAMMA_POSTURE_SHIFT
    if (prev.gammaRegime && curr.gammaRegime && prev.gammaRegime !== curr.gammaRegime) {
      push('gamma_posture_change', 'medium',
        'RULE_GAMMA_POSTURE_SHIFT',
        `Gamma regime: "${prev.gammaRegime}" → "${curr.gammaRegime}"`);
    }

    // RULE_VOLATILITY_CHANGE
    if (prev.volatilityRegime && curr.volatilityRegime && prev.volatilityRegime !== curr.volatilityRegime) {
      const toHigh = curr.volatilityRegime === 'high_vol' || curr.volatilityRegime === 'extreme_vol';
      push('volatility_regime_change', toHigh ? 'high' : 'medium',
        'RULE_VOLATILITY_CHANGE',
        `Vol regime: ${prev.volatilityRegime} → ${curr.volatilityRegime}`);
    }

    // RULE_MARKET_STATE_SHIFT
    if (prev.marketState !== curr.marketState && curr.marketState && prev.marketState) {
      push('market_state_shift', 'info',
        'RULE_MARKET_STATE_SHIFT',
        `Market state: "${prev.marketState}" → "${curr.marketState}"`);
    }

    // RULE_WALL_SHIFT (call wall or put wall moved significantly)
    if (curr.price != null && curr.callWall != null && prev.callWall != null) {
      const pct = Math.abs(curr.callWall - prev.callWall) / curr.price;
      if (pct >= T.WALL_SHIFT_MIN_PCT) {
        push('wall_shift', 'info',
          'RULE_WALL_SHIFT_CALL',
          `Call wall shifted: ${prev.callWall.toFixed(2)} → ${curr.callWall.toFixed(2)}`);
      }
    }
    if (curr.price != null && curr.putWall != null && prev.putWall != null) {
      const pct = Math.abs(curr.putWall - prev.putWall) / curr.price;
      if (pct >= T.WALL_SHIFT_MIN_PCT) {
        push('wall_shift', 'info',
          'RULE_WALL_SHIFT_PUT',
          `Put wall shifted: ${prev.putWall.toFixed(2)} → ${curr.putWall.toFixed(2)}`);
      }
    }

    // ── TEXT FALLBACK — only when structured data is unavailable ─────────────
    // breakout text: only if structured regime/trap fields did NOT already fire
    const structuredBreakoutFired = events.some(
      e => e.snapshotIndex === idx && (e.type === 'regime_change' || e.type === 'trap_triggered')
    );
    if (!structuredBreakoutFired && _hasBreakoutText(curr.summary)) {
      push('breakout_text', 'info',
        'RULE_BREAKOUT_TEXT',
        `Breakout context in summary: "${curr.summary.slice(0, 80)}"`,
        'text_fallback');
    }
  }

  // Stable sort: by ts, then by snapshotIndex, then by event type (alphabetical)
  return events.sort((a, b) =>
    a.ts - b.ts || a.snapshotIndex - b.snapshotIndex || a.type.localeCompare(b.type)
  );
}

// ── Step 2: Turning points ────────────────────────────────────────────────────

function _extractTurningPoints(events: ReplayEvent[], snapshots: ReplayIntelligenceSnapshot[]): TurningPoint[] {
  const byIdx = new Map<number, ReplayEvent[]>();
  for (const e of events) {
    const bucket = byIdx.get(e.snapshotIndex) ?? [];
    bucket.push(e);
    byIdx.set(e.snapshotIndex, bucket);
  }

  const points: TurningPoint[] = [];
  for (const [idx, es] of byIdx.entries()) {
    // Count distinct event types (not raw event count, to avoid wall_shift spam)
    const distinctTypes = new Set(es.map(e => e.type)).size;
    if (distinctTypes < T.TURNING_POINT_MAJOR) continue;

    const severity: TurningPointSeverity = distinctTypes >= T.TURNING_POINT_CRITICAL ? 'critical' : 'major';
    const highEvents = es.filter(e => e.severity === 'high');
    const lead = highEvents[0] ?? es[0];
    const snap = snapshots[idx];
    const extra = distinctTypes - 1;
    const narrative = `${lead.reason}${extra > 0 ? ` (+${extra} other signal${extra > 1 ? 's' : ''})` : ''} — bias=${snap.bias} conv=${snap.conviction}%`;

    points.push({ snapshotIndex: idx, ts: lead.ts, severity, events: es, narrative });
  }

  return points.sort((a, b) => a.ts - b.ts);
}

// ── Step 3: Structure evolution ───────────────────────────────────────────────

function _buildStructureEvo(snapshots: ReplayIntelligenceSnapshot[], wallShiftEvents: number): ReplayIntelligenceSummary['structureEvo'] {
  const callWalls  = snapshots.map(s => s.callWall).filter((v): v is number => v != null);
  const putWalls   = snapshots.map(s => s.putWall).filter((v): v is number => v != null);
  const flipZones  = snapshots.map(s => s.flipZone).filter((v): v is number => v != null);
  const uniqueFlip = new Set(flipZones.map(v => v.toFixed(2))).size;

  return {
    callWallRange:   callWalls.length > 0  ? [Math.min(...callWalls), Math.max(...callWalls)]   : null,
    putWallRange:    putWalls.length > 0   ? [Math.min(...putWalls),  Math.max(...putWalls)]    : null,
    flipZoneShifts:  flipZones.length > 1  ? uniqueFlip - 1 : 0,
    wallShiftEvents,
  };
}

// ── Step 4: Session summary ───────────────────────────────────────────────────

function _buildSummary(
  symbol:        string,
  snapshots:     ReplayIntelligenceSnapshot[],
  events:        ReplayEvent[],
  turningPoints: TurningPoint[],
): ReplayIntelligenceSummary | null {
  if (snapshots.length < T.MIN_ENTRIES_FOR_SUMMARY) return null;

  // Dominant bias — structured count
  const biasCounts: Record<string, number> = {};
  for (const s of snapshots) biasCounts[s.bias] = (biasCounts[s.bias] ?? 0) + 1;
  const dominantBias = Object.entries(biasCounts).sort((a, b) => b[1] - a[1])[0][0];

  // Dominant regime — from structured field
  const regimeCounts: Record<string, number> = {};
  for (const s of snapshots) {
    if (s.regimeDominant) regimeCounts[s.regimeDominant] = (regimeCounts[s.regimeDominant] ?? 0) + 1;
  }
  const dominantRegime = Object.keys(regimeCounts).length > 0
    ? Object.entries(regimeCounts).sort((a, b) => b[1] - a[1])[0][0]
    : null;

  // Dominant volatility regime
  const volCounts: Record<string, number> = {};
  for (const s of snapshots) {
    if (s.volatilityRegime) volCounts[s.volatilityRegime] = (volCounts[s.volatilityRegime] ?? 0) + 1;
  }
  const dominantVolRegime = Object.keys(volCounts).length > 0
    ? Object.entries(volCounts).sort((a, b) => b[1] - a[1])[0][0]
    : null;

  // Conviction stats
  const convs = snapshots.map(s => s.conviction);
  const avgConviction    = _round1(convs.reduce((a, b) => a + b, 0) / convs.length);
  const peakConviction   = Math.max(...convs);
  const troughConviction = Math.min(...convs);

  // Conviction trend
  const mid          = Math.floor(snapshots.length / 2);
  const firstAvg     = convs.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
  const secondAvg    = convs.slice(mid).reduce((a, b) => a + b, 0) / (convs.length - mid);
  const trendΔ       = secondAvg - firstAvg;
  const convictionTrend: ConvictionTrend = trendΔ >= T.CONVICTION_TREND_DELTA ? 'rising'
    : trendΔ <= -T.CONVICTION_TREND_DELTA ? 'falling' : 'stable';

  const strongestSnapshot   = snapshots.reduce((b, s) => s.conviction > b.conviction ? s : b, snapshots[0]);
  const highestRiskSnapshot = snapshots.reduce((w, s) => s.actionability < w.actionability ? s : w, snapshots[0]);
  const majorTurningPoint   = turningPoints.find(p => p.severity === 'critical') ?? turningPoints.find(p => p.severity === 'major') ?? null;

  // Session label
  const hasHighVol   = dominantVolRegime === 'high_vol' || dominantVolRegime === 'extreme_vol';
  const hasTurning   = turningPoints.some(p => p.severity === 'critical');
  const hasTrapEvents = events.some(e => e.type === 'trap_triggered');
  const sessionLabel = (() => {
    if (hasTurning && hasTrapEvents)                                         return 'Trap-driven reversal session';
    if (dominantBias === 'bullish' && convictionTrend === 'rising')          return 'Bullish conviction build';
    if (dominantBias === 'bullish' && convictionTrend === 'falling')         return 'Bullish fade / exhaustion';
    if (dominantBias === 'bearish' && convictionTrend === 'rising')          return 'Bearish conviction build';
    if (dominantBias === 'bearish' && convictionTrend === 'falling')         return 'Bearish fade / reversion';
    if (hasHighVol)                                                           return 'Elevated volatility session';
    if (hasTurning)                                                           return 'High-instability session';
    if (dominantBias === 'neutral')                                           return 'Indecisive / ranging session';
    return 'Mixed / transitional session';
  })();

  // Lessons — from structured events, not text
  const lessons: string[] = [];
  const biasFlips    = events.filter(e => e.type === 'bias_flip' && e.severity === 'high');
  const regimeChanges = events.filter(e => e.type === 'regime_change');
  const traps        = events.filter(e => e.type === 'trap_triggered');
  const confDrops    = events.filter(e => e.type === 'confidence_drop' && e.severity === 'high');
  const volShifts    = events.filter(e => e.type === 'volatility_regime_change');

  if (biasFlips.length > 0)
    lessons.push(`${biasFlips.length} directional flip${biasFlips.length > 1 ? 's' : ''} — conviction did not sustain; confirm direction before committing`);
  if (traps.length > 0)
    lessons.push(`${traps.length} trap condition${traps.length > 1 ? 's' : ''} emerged — review structural context at these moments`);
  if (regimeChanges.length > 0)
    lessons.push(`${regimeChanges.length} regime transition${regimeChanges.length > 1 ? 's' : ''} — playbook alignment was required`);
  if (confDrops.length > 0)
    lessons.push(`${confDrops.length} conviction collapse${confDrops.length > 1 ? 's' : ''} — note what invalidated the setup`);
  if (volShifts.length > 0)
    lessons.push(`${volShifts.length} volatility regime shift${volShifts.length > 1 ? 's' : ''} — environment changed during session`);
  if (convictionTrend === 'rising' && dominantBias !== 'neutral')
    lessons.push(`Conviction built steadily — ${dominantBias} thesis strengthened`);
  if (convictionTrend === 'falling')
    lessons.push('Conviction faded — evaluate whether exit criteria were met in time');
  if (lessons.length === 0) lessons.push('Stable session — no major structural transitions detected');

  // Structure evolution
  const wallShiftCount = events.filter(e => e.type === 'wall_shift').length;
  const structureEvo   = _buildStructureEvo(snapshots, wallShiftCount);

  // Reasoning (stable order — no runtime calls)
  const reasoning: string[] = [
    `Session: ${snapshots.length} snapshots | dominant bias: ${dominantBias} | regime: ${dominantRegime ?? '—'} | trend: ${convictionTrend}`,
    `Conviction: avg=${avgConviction}% peak=${peakConviction}% trough=${troughConviction}%`,
    `Vol regime: ${dominantVolRegime ?? '—'} | events: ${events.length} | turning points: ${turningPoints.length}`,
    `Session label: ${sessionLabel}`,
    ...(majorTurningPoint ? [`Major turning point: ${majorTurningPoint.narrative}`] : []),
  ];

  return {
    symbol, entryCount: snapshots.length, sessionLabel,
    dominantBias, dominantRegime, dominantVolRegime,
    avgConviction, peakConviction, troughConviction, convictionTrend,
    majorTurningPoint, highestRiskSnapshot, strongestSnapshot,
    eventCount: events.length, turningPointCount: turningPoints.length,
    structureEvo, lessons, reasoning,
  };
}

// ── Main export: deterministic pure function ──────────────────────────────────

/**
 * Produce deterministic replay intelligence from a sorted snapshot array.
 *
 * PURE FUNCTION — no Date.now(), no I/O, no side effects.
 * Output is identical for identical inputs.
 *
 * @param symbol    — symbol being analysed
 * @param snapshots — ReplayIntelligenceSnapshot[] (primary) or adapted from ThesisHistoryEntry[]
 */
export function analyseSnapshots(
  symbol:    string,
  snapshots: ReplayIntelligenceSnapshot[],
): ReplayIntelligenceOutput {
  const sorted       = [...snapshots].sort((a, b) => a.ts - b.ts);
  const events       = _extractEvents(sorted);
  const turningPoints = _extractTurningPoints(events, sorted);
  const summary      = _buildSummary(symbol, sorted, events, turningPoints);

  return { symbol, snapshotCount: sorted.length, events, turningPoints, summary };
}

/**
 * Wrap deterministic output with a runtime timestamp for UI/cache use.
 * computedAt is NOT part of the deterministic output.
 */
export function analyseSnapshotsWithMeta(
  symbol:    string,
  snapshots: ReplayIntelligenceSnapshot[],
): ReplayIntelligenceResult {
  return {
    computedAt: Date.now(),   // runtime-only — separated from deterministic output
    output:     analyseSnapshots(symbol, snapshots),
  };
}

// ── Backward-compatible adapter ───────────────────────────────────────────────
//
// Converts ThesisHistoryEntry[] (the thesis-history-only path) into
// ReplayIntelligenceSnapshot[] for analysis.
// Structured fields that don't exist in ThesisHistoryEntry default to null/0.

export function fromThesisHistory(
  symbol:  string,
  history: ThesisHistoryEntry[],
): ReplayIntelligenceSnapshot[] {
  return history.map(h => ({
    ts:             h.timestamp,
    symbol,
    price:          null,
    bias:           h.bias,
    conviction:     h.conviction,
    actionability:  h.actionability,
    marketState:    h.marketState,
    summary:        h.summary ?? '',
    regimeDominant: null,
    regimeLabel:    null,
    regimeConfidence: null,
    gammaRegime:    null,
    gammaFlip:      null,
    trapCount:      0,
    trapConflict:   false,
    volatilityRegime: null,
    callWall:       null,
    putWall:        null,
    flipZone:       null,
  }));
}

/**
 * Backward-compatible entry point — accepts ThesisHistoryEntry[].
 * @deprecated Prefer fromResearchSnapshots() + analyseSnapshots() for richer data.
 */
export function analyseReplayHistory(
  symbol:  string,
  history: ThesisHistoryEntry[],
): ReplayIntelligenceOutput {
  return analyseSnapshots(symbol, fromThesisHistory(symbol, history));
}

// ── ResearchSnapshot adapter ──────────────────────────────────────────────────
//
// Converts ResearchSnapshot[] into ReplayIntelligenceSnapshot[].
// This is the preferred path — uses all available structured fields.

export function fromResearchSnapshots(
  symbol:    string,
  snapshots: ResearchSnapshot[],
): ReplayIntelligenceSnapshot[] {
  return snapshots
    .filter(s => s.symbol === symbol)
    .map(s => ({
      ts:             s.createdAt,
      symbol,
      price:          s.marketState.price,
      bias:           s.thesis?.overallBias ?? 'neutral',
      conviction:     s.thesis?.conviction  ?? 0,
      actionability:  s.setup?.confidence   ?? 0,
      marketState:    s.thesis?.marketState ?? s.marketState.regimeLabel ?? '—',
      summary:        s.thesis?.primaryThesis ?? '',
      regimeDominant: s.marketState.regimeDominant,
      regimeLabel:    s.marketState.regimeLabel,
      regimeConfidence: s.marketState.regimeConfidence,
      gammaRegime:    s.evidence.gammaRegime,
      gammaFlip:      s.evidence.gammaFlip,
      trapCount:      s.evidence.trapCount,
      trapConflict:   s.risk?.trapConflict ?? false,
      volatilityRegime: null,   // not yet in ResearchSnapshot — future extension
      callWall:       null,     // not yet in ResearchSnapshot — future extension
      putWall:        null,
      flipZone:       s.evidence.gammaFlip,
    }));
}
